<template>
  <transition name="notification">
    <div
      v-if="show"
      class="notification"
      :class="isError ? 'notification-error' : 'notification-success'"
    >
      <div class="notification-message">{{ message }}</div>
      <button class="notification-close" @click="closeNotification">&times;</button>
    </div>
  </transition>
</template>

<script setup>
import { watch } from "vue";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: "",
  },
  isError: {
    type: Boolean,
    default: false,
  },
  autoHide: {
    type: Boolean,
    default: true,
  },
  duration: {
    type: Number,
    default: 3000,
  },
});

const emit = defineEmits(["close"]);

let timeout = null;

const closeNotification = () => {
  emit("close");
};

// Auto-hide functionality
watch(
  () => props.show,
  (newShow) => {
    if (newShow && props.autoHide) {
      if (timeout) {
        clearTimeout(timeout);
      }
      const hideDelay = props.isError ? 4000 : props.duration;
      timeout = setTimeout(() => {
        closeNotification();
      }, hideDelay);
    } else if (!newShow && timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  }
);
</script>

<style scoped>
.notification {
  position: fixed;
  top: var(--spacing-notification);
  right: var(--spacing-notification);
  padding: var(--padding-notification);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-notification);
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: var(--z-index-notification);
  max-width: var(--max-width-notification);
  animation: slide-in var(--transition-normal) ease-out forwards;
}

.notification-success {
  background: var(--gradient-success);
  color: var(--color-white);
}

.notification-error {
  background: var(--gradient-error);
  color: var(--color-white);
}

.notification-message {
  margin-right: var(--margin-md);
  font-size: var(--font-size-caption);
  line-height: var(--line-height-normal);
}

.notification-close {
  cursor: pointer;
  background: none;
  border: none;
  color: var(--color-white);
  font-size: var(--font-size-large);
  line-height: 1;
  padding: 0;
  margin-left: var(--gap-lg);
  opacity: 0.8;
  transition: opacity var(--transition-fast);
}

.notification-close:hover {
  opacity: 1;
}

.notification-enter-active,
.notification-leave-active {
  transition: all var(--transition-normal) ease;
}

.notification-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.notification-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

@keyframes slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
